import { useCallback, useMemo } from 'react';
import { useUserConsent } from '@shared/utils/userConsent/useUserConsent';
import { useWebsitePages } from '@shared/hooks/useWebsitePages/useWebsitePages';

interface UseAccommodationsVisibilityBannerArgs
  extends Readonly<{
    hasIdentifiedUserOnce: boolean;
    isAdmin: boolean;
    isPreviewing: boolean;
    isWebsitePublishCapabilityEnabled: boolean;
    isWebsitePublished: boolean;
  }> {}

export const useAccommodationsVisibilityBanner = (args: UseAccommodationsVisibilityBannerArgs) => {
  const { hasIdentifiedUserOnce, isAdmin, isPreviewing, isWebsitePublishCapabilityEnabled, isWebsitePublished } = args;

  const { getUserConsent, setUserConsent } = useUserConsent();
  const { getPageByName } = useWebsitePages();

  // Check if accommodations page exists and is hidden
  const accommodationsPage = getPageByName('accommodations');
  const isAccommodationsPageHidden = !accommodationsPage || accommodationsPage.visibility === 'hidden';

  // Check if user has already dismissed the banner
  const hasSeenAccommodationsVisibilityBanner = getUserConsent('hasSeenAccommodationsVisibilityBanner');

  // Determine if banner should be shown
  const showAccommodationsVisibilityBanner = useMemo(
    () =>
      !hasSeenAccommodationsVisibilityBanner &&
      hasIdentifiedUserOnce &&
      isAdmin &&
      !isPreviewing &&
      ((isWebsitePublishCapabilityEnabled && isWebsitePublished) || !isWebsitePublishCapabilityEnabled) &&
      isAccommodationsPageHidden,
    [hasSeenAccommodationsVisibilityBanner, hasIdentifiedUserOnce, isAdmin, isPreviewing, isWebsitePublishCapabilityEnabled, isWebsitePublished, isAccommodationsPageHidden],
  );

  const handleCloseAccommodationsVisibilityBanner = useCallback(() => {
    setUserConsent('hasSeenAccommodationsVisibilityBanner', true);
  }, [setUserConsent]);

  return {
    showAccommodationsVisibilityBanner,
    handleCloseAccommodationsVisibilityBanner,
  };
};
